import React, { useState, useEffect } from 'react';
import { Briefcase, TrendingUp, ShieldCheck, Award, Users, Search, FileText, BarChart, ArrowLeft, CheckCircle2, Save, Edit, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../components/UI/Button';
import { api } from '../services/api';
import { SiteContent, ServicesContent } from '../types';
import { useAuth } from '../context/AuthContext';
import EditableContent from '../components/Admin/EditableContent';

const IconMap: any = {
  BarChart: BarChart,
  TrendingUp: TrendingUp,
  ShieldCheck: ShieldCheck,
  Briefcase: Briefcase,
  Award: Award,
  Search: Search,
  Users: Users,
  FileText: FileText
};

const ServicesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [content, setContent] = useState<SiteContent | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const pageContent = await api.getPageContent();
      setContent(pageContent);
    };
    fetchData();
  }, []);

  const handleSaveContent = async () => {
    if (!content) return;
    setSaving(true);
    await api.updatePageContent(content);
    setSaving(false);
    setIsEditing(false);
  };

  const updateServicesContent = (section: keyof ServicesContent, key: string, value: any) => {
    if (!content) return;
    setContent({
      ...content,
      services: {
        ...content.services,
        [section]: typeof content.services[section] === 'object' && !Array.isArray(content.services[section])
          ? { ...(content.services[section] as any), [key]: value }
          : value
      }
    });
  };

  const updateServiceItem = (index: number, field: string, value: any) => {
    if (!content) return;
    const newItems = [...content.services.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setContent({
        ...content,
        services: {
            ...content.services,
            items: newItems
        }
    });
  };

  const updateFeature = (serviceIdx: number, featureIdx: number, value: string) => {
    if (!content) return;
    const newItems = [...content.services.items];
    const newFeatures = [...newItems[serviceIdx].features];
    newFeatures[featureIdx] = value;
    newItems[serviceIdx] = { ...newItems[serviceIdx], features: newFeatures };
    setContent({
        ...content,
        services: {
            ...content.services,
            items: newItems
        }
    });
  };

  if (!content || !content.services) return <div className="min-h-screen flex items-center justify-center">در حال بارگذاری...</div>;

  const isAdmin = user?.role === 'admin';
  const { services } = content;

  return (
    <div className="bg-gray-50 min-h-screen pb-20 relative">
      {/* Admin Toolbar */}
      {isAdmin && (
        <div className="fixed bottom-6 left-6 z-50 flex gap-2">
            {isEditing ? (
                <>
                    <Button onClick={handleSaveContent} disabled={saving} className="shadow-xl bg-green-600 hover:bg-green-700 flex items-center gap-2">
                        <Save size={18}/> {saving ? 'ذخیره...' : 'ذخیره تغییرات'}
                    </Button>
                    <Button onClick={() => { setIsEditing(false); window.location.reload(); }} variant="ghost" className="bg-white shadow-xl text-red-500 hover:bg-red-50">
                        <X size={18}/>
                    </Button>
                </>
            ) : (
                <Button onClick={() => setIsEditing(true)} className="shadow-xl bg-gray-900 hover:bg-gray-800 flex items-center gap-2">
                    <Edit size={18}/> ویرایش صفحه
                </Button>
            )}
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-[#1e3a5f] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-black mb-6">
            <EditableContent 
                isEditing={isEditing}
                value={services.hero.title}
                onChange={(val) => updateServicesContent('hero', 'title', val)}
                className="text-center bg-transparent border-white/20"
            />
          </h1>
          <div className="text-lg text-blue-100 max-w-3xl mx-auto leading-relaxed">
            <EditableContent 
                isEditing={isEditing}
                type="textarea"
                value={services.hero.subtitle}
                onChange={(val) => updateServicesContent('hero', 'subtitle', val)}
                className="text-center bg-transparent border-white/20"
            />
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.items.map((service, idx) => {
            const Icon = IconMap[service.icon] || BarChart;
            return (
              <div key={service.id} className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 hover:shadow-xl transition-all group flex flex-col">
                <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  {isEditing ? (
                    <select 
                        value={service.icon}
                        onChange={(e) => updateServiceItem(idx, 'icon', e.target.value)}
                        className="bg-transparent text-[10px] w-full outline-none"
                    >
                        {Object.keys(IconMap).map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                  ) : <Icon size={32} />}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                    <EditableContent 
                        isEditing={isEditing}
                        value={service.title}
                        onChange={(val) => updateServiceItem(idx, 'title', val)}
                    />
                </h3>
                <div className="text-gray-600 leading-relaxed mb-6">
                    <EditableContent 
                        isEditing={isEditing}
                        type="textarea"
                        value={service.description}
                        onChange={(val) => updateServiceItem(idx, 'description', val)}
                    />
                </div>

                <div className="mt-auto">
                    {service.features && service.features.length > 0 && (
                        <ul className="space-y-2 mb-6">
                            {service.features.map((feature, fIdx) => (
                                <li key={fIdx} className="text-sm text-gray-500 flex items-center gap-2">
                                    <CheckCircle2 size={14} className="text-green-500" />
                                    <EditableContent 
                                        isEditing={isEditing}
                                        value={feature}
                                        onChange={(val) => updateFeature(idx, fIdx, val)}
                                    />
                                </li>
                            ))}
                        </ul>
                    )}
                    <Button 
                        variant="outline" 
                        className="w-full border-gray-200 text-gray-700 hover:border-[#1e3a5f] hover:text-[#1e3a5f] rounded-xl"
                        onClick={() => navigate(service.id === '1' ? '/valuation' : '/about')}
                    >
                        اطلاعات بیشتر
                    </Button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="bg-white p-10 rounded-3xl shadow-lg border border-gray-100 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">نیاز به مشاوره اختصاصی دارید؟</h2>
          <p className="text-gray-600 mb-8">
            کارشناسان ما آماده پاسخگویی به سوالات شما و ارائه راهکارهای متناسب با نیاز کسب‌وکارتان هستند.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl px-10"
              onClick={() => navigate('/contact')}
            >
              تماس با ما
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-blue-50 rounded-xl px-10"
              onClick={() => navigate('/listings')}
            >
              مشاهده آگهی‌ها
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
